import { makeAutoObservable, runInAction } from "mobx";

import { Couriers } from "../Delivery/CourierShipmentResponseModel";
import { orderAjaxController } from "../../../Common/Ajax/OrderAjaxController";
import { StockistPurchaseOrderItemModel } from "./StockistPurchaseOrderItemModel";

export class StockistPurchaseModel {
  readonly id: number;
  readonly stockistId: number;
  readonly transactionId: number;
  readonly addressId: number;
  readonly currency: string;
  readonly status: StockistPurchaseStatus;
  readonly courier: Couriers;
  readonly serviceType: string;
  readonly eori: string;
  readonly declaredDelivery: Date;
  readonly saturdayDelivery: boolean;
  readonly costWithoutVat: number;
  readonly vat: number;
  readonly publisherPrice: number;
  readonly transactionValue: number;
  readonly commercialInvoiceLink: string;
  readonly trackingNumber: string;
  readonly currencyText: string;
  readonly currencySymbol: string;
  readonly statusText: string;
  readonly trackingNumberUrl: string;
  readonly hasInvoice: boolean;
  readonly lastAction: Date;

  items: StockistPurchaseOrderItemModel[] = [];
  isExpanded = false;
  isItemsLoading = false;

  constructor(dto: {
    id: number;
    stockistId: number;
    transactionId: number;
    addressId: number;
    currency: string;
    status: StockistPurchaseStatus;
    courier: Couriers;
    serviceType: string;
    eori: string;
    declaredDelivery: Date;
    saturdayDelivery: boolean;
    costWithoutVat: number;
    vat: number;
    publisherPrice: number;
    transactionValue: number;
    commercialInvoiceLink: string;
    trackingNumber: string;
    currencyText: string;
    currencySymbol: string;
    statusText: string;
    trackingNumberUrl: string;
    hasInvoice: boolean;
    lastAction: Date;
  }) {
    this.id = dto.id;
    this.stockistId = dto.stockistId;
    this.transactionId = dto.transactionId;
    this.addressId = dto.addressId;
    this.currency = dto.currency;
    this.status = dto.status;
    this.courier = dto.courier;
    this.serviceType = dto.serviceType;
    this.eori = dto.eori;
    this.declaredDelivery = new Date(dto.declaredDelivery);
    this.saturdayDelivery = dto.saturdayDelivery;
    this.costWithoutVat = dto.costWithoutVat;
    this.vat = dto.vat;
    this.publisherPrice = dto.publisherPrice;
    this.transactionValue = dto.transactionValue;
    this.commercialInvoiceLink = dto.commercialInvoiceLink;
    this.trackingNumber = dto.trackingNumber;
    this.currencyText = dto.currencyText;
    this.currencySymbol = dto.currencySymbol;
    this.statusText = dto.statusText;
    this.trackingNumberUrl = dto.trackingNumberUrl;
    this.hasInvoice = dto.hasInvoice;
    this.lastAction = new Date(dto.lastAction);
    makeAutoObservable(this);
  }

  async toggleExpanded() {
    this.isExpanded = !this.isExpanded;
    if (this.isExpanded && this.items.length === 0) {
      await this.fetchPurchaseOrderItems();
    }
  }

  async fetchPurchaseOrderItems() {
    runInAction(() => {
      this.isItemsLoading = true;
    });
    try {
      const items = await orderAjaxController.getStockistOrderItems(this.id);
      runInAction(() => {
        this.items = items;
      });
    } finally {
      runInAction(() => {
        this.isItemsLoading = false;
      });
    }
  }

  async openInvoiceLink() {
    try {
      await orderAjaxController.downloadInvoice(this.id);
    } catch (error) {
      console.error('Error downloading invoice:', error);
    }
  }
}

export enum StockistPurchaseStatus {
  Pending = "Pending",
  Failed = "Failed",
  Successful = "Successful",
  Completed = "Completed",
  Cancelled = "Cancelled",
}